import React, { useMemo, useState } from 'react';
import {
    ArrowSquareOut,
    Article,
    BatteryHigh,
    CaretRight,
    CloudArrowUp,
    FileText,
    House,
    Package,
    SealCheck,
    Sun,
    TreeStructure,
    Wrench,
} from '@phosphor-icons/react';
import type { PreContractProduct } from '../lib/preContractTypes';
import { useOneButtonStore } from '../../store/oneButtonStore';
import {
    finalizeAgreementRowsFromQuoteContract,
    AgreementsModal,
    type AgreementRow,
} from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/AgreementsModal';
import { useFinanceAgreementMilestone } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/financeAgreementMilestone';
import OBDQuoteDetailsModal from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/OBDQuoteDetailsModal';
import { ViewTrinityContractModal } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/ViewTrinityContractModal';
import {
    agreementRowToTrinityRow,
    buildSignedProductAgreements,
    contractDocumentUrl,
    formatSfdcSyncDisplay,
    quoteDetailsProductLabelFromRow,
} from '../lib/preContractCommerce';
import '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/oneButton-css/ViewTrinityContractModal.css';

const PRODUCT_ICONS: Record<PreContractProduct, React.ReactNode> = {
    Solar: <Sun size={18} weight="duotone" aria-hidden />,
    Roof: <House size={18} weight="duotone" aria-hidden />,
    Battery: <BatteryHigh size={18} weight="duotone" aria-hidden />,
    RR: <Wrench size={18} weight="duotone" aria-hidden />,
    RRR: <TreeStructure size={18} weight="duotone" aria-hidden />,
};

const PRODUCT_ACCENTS: Record<PreContractProduct, string> = {
    Solar: 'amber',
    Roof: 'slate',
    Battery: 'green',
    RR: 'blue',
    RRR: 'violet',
};

export type PreContractCommerceSectionProps = {
    selectedProducts: PreContractProduct[];
    onOpenDashboardV2: () => void;
};

type QuoteDetailsState = {
    quoteId: string;
    partner: string;
    product: string;
    hoverEstimateID?: string | number | null;
};

function AgreementProductCard({
    product,
    row,
    onViewQuote,
}: {
    product: PreContractProduct;
    row: AgreementRow;
    onViewQuote: (row: AgreementRow) => void;
}) {
    const accent = PRODUCT_ACCENTS[product];

    return (
        <article
            className={`pci-commerce-agreement-card pci-commerce-agreement-card--${accent} pci-commerce-agreement-card--signed`}
            aria-label={`${product} agreement`}
        >
            <header className="pci-commerce-agreement-card__head">
                <span className="pci-commerce-agreement-card__icon">{PRODUCT_ICONS[product]}</span>
                <div className="pci-commerce-agreement-card__titles">
                    <h3>{product}</h3>
                    <p>{row.financePartner}</p>
                </div>
                <span className="pci-commerce-agreement-card__status pci-commerce-agreement-card__status--signed">
                    <SealCheck size={12} weight="fill" aria-hidden />
                    Signed
                </span>
            </header>

            <div className="pci-commerce-agreement-card__body">
                <div className="pci-commerce-agreement-card__metric">
                    <span>Quote</span>
                    <strong>{row.quoteId && row.quoteId !== '—' ? row.quoteId : '—'}</strong>
                </div>
                <div className="pci-commerce-agreement-card__metric">
                    <span>EPC</span>
                    <strong>{row.totalEpcDisplay && row.totalEpcDisplay !== '—' ? row.totalEpcDisplay : '—'}</strong>
                </div>
            </div>

            <footer className="pci-commerce-agreement-card__foot">
                <button
                    type="button"
                    className="pci-commerce-btn pci-commerce-btn--primary"
                    disabled={!row.quoteIdRaw}
                    onClick={() => onViewQuote(row)}
                >
                    Quote details
                </button>
            </footer>
        </article>
    );
}

export const PreContractCommerceSection: React.FC<PreContractCommerceSectionProps> = ({
    selectedProducts,
    onOpenDashboardV2,
}) => {
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const salesforceId = useOneButtonStore((s) => s.salesForceId);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const milestone = useFinanceAgreementMilestone(opportunityId);

    const [quoteDetails, setQuoteDetails] = useState<QuoteDetailsState | null>(null);
    const [trinityRow, setTrinityRow] = useState<AgreementRow | null>(null);
    const [agreementsOpen, setAgreementsOpen] = useState(false);

    const agreementRows = useMemo(() => {
        const raw = milestone.quoteContracts ?? [];
        return finalizeAgreementRowsFromQuoteContract(raw);
    }, [milestone.quoteContracts]);

    const signedProductRows = useMemo(
        () =>
            buildSignedProductAgreements(
                selectedProducts,
                agreementRows,
                opportunityDetails,
                selectedFinanceCells,
            ),
        [agreementRows, opportunityDetails, selectedFinanceCells, selectedProducts],
    );

    const openQuoteDetails = (row: AgreementRow) => {
        if (!row.quoteIdRaw) return;
        setQuoteDetails({
            quoteId: row.quoteIdRaw,
            partner: row.financePartnerRaw || row.financePartner,
            product: quoteDetailsProductLabelFromRow(row),
            hoverEstimateID: row.hoverEstimateID,
        });
    };

    if (!selectedProducts.length || milestone.loading || signedProductRows.length === 0) {
        return null;
    }

    return (
        <>
            <section className="pci-unified-deal pci-unified-commerce" aria-label="Agreements and commerce">
                <header className="pci-unified-commerce__head">
                    <div>
                        <h2 className="pci-unified-deal__title">Agreements &amp; quotes</h2>
                        <p className="pci-unified-deal__subtitle">
                            Signed agreements, quote details, and commerce actions by product
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pci-commerce-v2-btn"
                        onClick={onOpenDashboardV2}
                    >
                        Open Dashboard V2
                        <CaretRight size={14} weight="bold" aria-hidden />
                    </button>
                </header>

                <div className="pci-unified-commerce__grid">
                    <div className="pci-unified-commerce__agreements">
                        <h3 className="pci-unified-commerce__section-label">Signed agreements</h3>
                        <div className="pci-commerce-agreement-cards">
                            {signedProductRows.map(({ product, row }) => (
                                <AgreementProductCard
                                    key={product}
                                    product={product}
                                    row={row}
                                    onViewQuote={openQuoteDetails}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="pci-unified-commerce__actions">
                        <h3 className="pci-unified-commerce__section-label">Commerce by product</h3>
                        <div className="pci-commerce-actions-table-wrap">
                            <table className="pci-commerce-actions-table">
                                <thead>
                                    <tr>
                                        <th scope="col">Product</th>
                                        <th scope="col">Combined pack</th>
                                        <th scope="col">SFDC sync</th>
                                        <th scope="col">Contract document</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signedProductRows.map(({ product, row }) => {
                                        const docUrl = contractDocumentUrl(row);
                                        const sfdcLabel = formatSfdcSyncDisplay(row);
                                        return (
                                            <tr key={product}>
                                                <th scope="row">
                                                    <span className="pci-commerce-actions-table__product">
                                                        {PRODUCT_ICONS[product]}
                                                        {product}
                                                    </span>
                                                </th>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="pci-commerce-btn pci-commerce-btn--ghost"
                                                        onClick={() => setTrinityRow(row)}
                                                    >
                                                        <Package size={14} weight="duotone" aria-hidden />
                                                        Open combined pack
                                                    </button>
                                                </td>
                                                <td>
                                                    <div className="pci-commerce-actions-table__sfdc">
                                                        <span
                                                            className={`pci-commerce-pill${
                                                                sfdcLabel.toLowerCase().includes('sync')
                                                                    ? ' pci-commerce-pill--ok'
                                                                    : ''
                                                            }`}
                                                        >
                                                            <CloudArrowUp size={12} weight="bold" aria-hidden />
                                                            {sfdcLabel}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="pci-commerce-btn pci-commerce-btn--link"
                                                            onClick={() => setAgreementsOpen(true)}
                                                        >
                                                            Manage sync
                                                        </button>
                                                    </div>
                                                </td>
                                                <td>
                                                    {docUrl ? (
                                                        <a
                                                            className="pci-commerce-doc-link"
                                                            href={docUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <FileText size={14} weight="duotone" aria-hidden />
                                                            View document
                                                            <ArrowSquareOut size={12} weight="bold" aria-hidden />
                                                        </a>
                                                    ) : (
                                                        <span className="pci-commerce-actions-table__empty">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <button
                            type="button"
                            className="pci-commerce-agreements-link"
                            onClick={() => setAgreementsOpen(true)}
                        >
                            <Article size={16} weight="duotone" aria-hidden />
                            Open full agreements workspace
                        </button>
                    </div>
                </div>
            </section>

            {quoteDetails ? (
                <OBDQuoteDetailsModal
                    quoteId={quoteDetails.quoteId}
                    partner={quoteDetails.partner}
                    product={quoteDetails.product}
                    monthly={0}
                    apr={0}
                    hoverEstimateID={quoteDetails.hoverEstimateID}
                    mode="view"
                    onClose={() => setQuoteDetails(null)}
                    opportunityId={opportunityId}
                    salesforceId={salesforceId}
                    opportunityDetails={opportunityDetails}
                />
            ) : null}

            {trinityRow ? (
                <ViewTrinityContractModal
                    open
                    onClose={() => setTrinityRow(null)}
                    row={agreementRowToTrinityRow(trinityRow)}
                    opportunityId={opportunityId}
                    salesforceId={salesforceId}
                    opportunityDetails={opportunityDetails}
                />
            ) : null}

            {agreementsOpen && opportunityId ? (
                <AgreementsModal
                    open
                    onClose={() => setAgreementsOpen(false)}
                    opportunityId={opportunityId}
                />
            ) : null}
        </>
    );
};
